/**
 * Ein Projekt aufgeklappt.
 *
 * Die Geldzahlen kommen ausnahmslos aus `ProjectRow` — die fruehere
 * Nachrechnung im Dialog (eigene Summen aus `project_services`) ist entfallen,
 * sie konnte von der Kachel abweichen. Aus den Positionszeilen werden nur noch
 * Mengen und die Pauschalen-Deckung gelesen.
 */
import React, { useMemo } from 'react';
import { Button } from '../Button';
import type { ServiceItem } from '../../types';
import type { PerformedDetail, ProjectRow } from '../../services/cockpitTypes';
import { flatCoverageHints } from '../../services/flatRateLogic';
import { bestandteilFelderAusZeile, openQuantity, waivedQuantity } from '../../services/projectLv';
import { contractedQuantityOfRow } from '../../services/nepPosition';
import { assignmentLabel } from '../../services/assignmentLabels';
import { Bar, ModalShell } from './CockpitBits';
import { fmtCents, fmtCentsExact, pctOf, statusChip, type CockpitService } from './cockpitUi';

const num = (v: number | string | null | undefined): number => Number(v) || 0;

export interface ProjectDrilldownProps {
    project: ProjectRow;
    projectNumber: string;
    services: CockpitService[];
    performed: PerformedDetail[];
    hideAmounts: boolean;
    periodText: string;
    onClose: () => void;
}

export const ProjectDrilldown: React.FC<ProjectDrilldownProps> = ({
    project, projectNumber, services, performed, hideAmounts, periodText, onClose,
}) => {
    const chip = statusChip(project.status);
    const money = (c: number) => fmtCents(c, hideAmounts);

    // Die Liste hier sind rohe project_services-Zeilen ohne Deckungsstempel —
    // die Deckung deshalb frisch aufloesen, damit gedeckte Positionen nicht als
    // offen erscheinen.
    const flatHints = useMemo(() => flatCoverageHints(services.map((s) => ({
        id: String(s.id),
        assignment: s.assignment ?? undefined,
        lvPosition: s.lv_position ?? undefined,
        title: s.title ?? undefined,
        flatRate: s.flat_rate ?? undefined,
    }))), [services]);

    const progress = pctOf(project.performedLifetimeCents, project.contractCents);

    const kpis: { label: string; value: string; hint?: string; tone?: string }[] = [
        { label: 'Vertragswert', value: money(project.contractCents), hint: 'beauftragt × Preis' },
        { label: `Erbracht ${periodText}`, value: money(project.performedCents), hint: `gesamt ${money(project.performedLifetimeCents)}`, tone: 'text-emerald-700' },
        { label: `Abgerechnet ${periodText}`, value: money(project.billedCents), hint: 'netto aus Belegen', tone: 'text-blue-700' },
        { label: 'Noch abzurechnen', value: money(project.unbilledCents), hint: 'Stichtag heute', tone: 'text-amber-700' },
        { label: 'Auftragsbestand', value: money(project.backlogCents), hint: 'Stichtag heute' },
        { label: `Forecast ${periodText}`, value: money(project.forecastCents), hint: `${project.inspections} Begehungen · ${project.hours} h` },
    ];

    return (
        <ModalShell
            title={project.name}
            width="max-w-3xl"
            onClose={onClose}
            subtitle={
                <>
                    <span className={`text-[11px] font-semibold px-2 py-0.5 rounded border ${chip.bg} ${chip.text} ${chip.border}`}>{chip.label}</span>
                    <span className="text-[11px] text-slate-300 font-mono">#{projectNumber}</span>
                    <span className="text-[11px] text-slate-300">{project.responsibleName}</span>
                    <span className="text-[11px] text-slate-300">{project.customerName}</span>
                    {project.daysRemaining !== null && (
                        <span className={`text-[11px] font-semibold ${project.daysRemaining <= 30 ? 'text-red-300' : 'text-amber-300'}`}>
                            {project.daysRemaining} Tage verbleibend
                        </span>
                    )}
                </>
            }
            footer={<><span className="text-[11px] text-slate-400">{services.length} Leistungspositionen</span><Button variant="primary" size="sm" onClick={onClose}>Schließen</Button></>}
        >
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {kpis.map((k) => (
                    <div key={k.label} className="p-3 rounded-xl border border-slate-200">
                        <div className="text-[11px] font-semibold text-slate-400 uppercase">{k.label}</div>
                        <div className={`text-lg font-semibold ${k.tone ?? 'text-slate-800'}`}>{k.value}</div>
                        {k.hint && <div className="text-[11px] text-slate-400">{k.hint}</div>}
                    </div>
                ))}
            </div>

            <div>
                <div className="flex justify-between text-[11px] font-semibold text-slate-500 mb-1">
                    <span>Fortschritt (erbracht gesamt gegen Vertragswert)</span>
                    <span>{progress}%</span>
                </div>
                <Bar value={progress} max={100} color={progress > 90 ? 'bg-amber-400' : 'bg-emerald-500'} height="h-2" />
            </div>

            <section>
                <h4 className="text-sm font-semibold text-slate-800 mb-3">Leistungspositionen</h4>
                <div className="space-y-2">
                    {services.length === 0 && (
                        <div className="py-6 text-center text-slate-400 text-[11px] border border-slate-100 rounded-xl">Keine Leistungspositionen</div>
                    )}
                    {services.map((s) => {
                        const contracted = contractedQuantityOfRow(s);
                        const used = num(s.quantity_used);
                        const billed = num(s.quantity_billed);
                        const price = num(s.unit_price);
                        const usePct = contracted > 0 ? Math.round((used / contracted) * 100) : 0;
                        const hint = flatHints.get(String(s.id));
                        // „Offen" ist dieselbe Rechnung wie in der Kachel „Noch
                        // abzurechnen": projectLv.openQuantity, also inklusive
                        // Verzicht und Pauschalendeckung. Eine eigene Formel
                        // hier hatte den Verzicht verschluckt und die Position
                        // dauerhaft als offen gezeigt.
                        const item: ServiceItem = {
                            id: String(s.id),
                            lvPosition: s.lv_position ?? '',
                            title: s.title ?? '',
                            unit: s.unit ?? '',
                            quantityContracted: contracted,
                            quantityUsed: used,
                            quantityBilled: billed,
                            quantityWaived: num(s.quantity_waived),
                            unitPrice: price,
                            status: 'open',
                            ...(hint ? { flatCoveredBy: hint } : {}),
                            // Paketposition: ohne Definition und Gate
                            // zeigte „Offen" hier etwas anderes als die
                            // Kachel — dieselbe Lesart wie jeder Ladeweg.
                            ...bestandteilFelderAusZeile(s),
                        };
                        const waived = waivedQuantity(item);
                        const open = openQuantity(item);
                        return (
                            <div key={s.id} className="p-3 rounded-xl border border-slate-200">
                                <div className="flex justify-between items-start gap-3 mb-2">
                                    <div className="min-w-0">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <span className="text-xs font-semibold text-slate-700">{s.title}</span>
                                            <span className="text-[11px] px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-600 border border-indigo-100 font-semibold">
                                                {assignmentLabel(s.assignment)}
                                            </span>
                                        </div>
                                        <div className="text-[11px] text-slate-400 mt-0.5">{s.unit} · {fmtCentsExact(Math.round(price * 100), hideAmounts)}/Einheit</div>
                                    </div>
                                    <div className="text-right shrink-0">
                                        <div className="text-xs font-semibold text-slate-700">{money(Math.round(contracted * price * 100))}</div>
                                        <div className="text-[11px] text-slate-400">Vertrag</div>
                                    </div>
                                </div>
                                <div className="flex items-center gap-4 text-[11px] flex-wrap">
                                    <span className="text-slate-500">Beauftragt: <b>{contracted}</b></span>
                                    <span className="text-emerald-600">Erbracht: <b>{used}</b></span>
                                    <span className="text-blue-600">Abgerechnet: <b>{billed}</b></span>
                                    {waived > 0 && <span className="text-slate-500">Verzicht: <b>{waived}</b></span>}
                                    <span className={`font-semibold ${open > 0 ? 'text-orange-500' : 'text-slate-400'}`}>Offen: {open}</span>
                                    {hint && (
                                        <span className="text-[11px] px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-600 border border-indigo-100 font-semibold" title={hint}>
                                            in der Monatspauschale enthalten
                                        </span>
                                    )}
                                </div>
                                <div className="mt-2">
                                    <Bar value={usePct} max={100} height="h-1" color={usePct > 90 ? 'bg-red-400' : usePct > 70 ? 'bg-amber-400' : 'bg-emerald-500'} />
                                </div>
                            </div>
                        );
                    })}
                </div>
            </section>

            {performed.length > 0 && (
                <section>
                    <h4 className="text-sm font-semibold text-slate-800 mb-3">Letzte Leistungserbringungen im Zeitraum</h4>
                    <div className="space-y-1">
                        {[...performed].sort((a, b) => b.date.localeCompare(a.date)).slice(0, 12).map((d, i) => (
                            <div key={`${d.serviceId}-${d.reference}-${i}`} className="flex justify-between items-center gap-3 p-2 rounded-lg border border-slate-100">
                                <div className="min-w-0">
                                    <div className="text-xs text-slate-700 truncate">{d.serviceTitle}</div>
                                    <div className="text-[11px] text-slate-400">
                                        {d.date.slice(8, 10)}.{d.date.slice(5, 7)}.{d.date.slice(0, 4)} · {d.performerName}
                                    </div>
                                </div>
                                <div className="text-right shrink-0">
                                    <div className="text-xs font-semibold text-emerald-700">{fmtCentsExact(d.cents, hideAmounts)}</div>
                                    <div className="text-[11px] text-slate-400">{d.quantity} {d.unit}</div>
                                </div>
                            </div>
                        ))}
                    </div>
                </section>
            )}
        </ModalShell>
    );
};
